import { useLazyQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { getAircraftList } from "../sharedCode/queries";
import { lookupOptions } from "../GQL/lookupOptions";
import { AircraftExtended, ExtendAircraft } from "../Utils/TypeExtensions";
import { BackgroundProps, SyncStatus, TableSyncer } from "../Utils/Interfaces";
import { TicketDatabase } from "../Utils/TicketDatabase";
import { ObjectUtils } from "../Utils/ObjectUtils";
import { Aircraft } from "../sharedCode/avroGraphql";

export default function useAircraftBackgroundPull(props: BackgroundProps): TableSyncer {
    const [status, setStatus] = useState(SyncStatus.Loading);
    const [doQuery, { loading: gqlLoading, error: gqlError, data: gqlResult, refetch: refetch }] = useLazyQuery(getAircraftList, lookupOptions);

    const fetchData = (firstRun: boolean) => {
        if (firstRun) {
            doQuery();
        } else {
            setStatus(SyncStatus.Loading);
            props.updateUi();
            refetch()
                .then((results) => {
                    const rawList = results.data.getAircraft.slice() as Aircraft[];
                    saveToLocalDatabase(rawList);
                    setStatus(SyncStatus.Fresh);
                    props.updateUi();
                })
                .catch((e) => {
                    setStatus(SyncStatus.Stale);
                    props.updateUi();
                });
        }
    };

    function saveToLocalDatabase(rawList: Aircraft[]) {
        const listToSave = ObjectUtils.convertToUppercase(rawList.map((item: Aircraft) => ExtendAircraft(item))) as AircraftExtended[];

        TicketDatabase.transaction("rw", TicketDatabase.aircraft, async () => {
            await TicketDatabase.aircraft
                ?.toCollection()
                .primaryKeys()
                .then((keys) => {
                    TicketDatabase.aircraft.bulkDelete(keys);
                })
                .then(() => TicketDatabase.aircraft.bulkAdd(listToSave));
        });
    }

    useEffect(() => {
        if (gqlResult) {
            const rawList = gqlResult.getAircraft.slice() as Aircraft[];
            saveToLocalDatabase(rawList);
            setStatus(SyncStatus.Fresh);
            props.updateUi();
        }
    }, [gqlResult]);

    useEffect(() => {
        if (gqlError) {
            TicketDatabase.aircraft.count().then((c) => {
                setStatus(c > 0 ? SyncStatus.Stale : SyncStatus.Error);
                props.updateUi();
            });
        }
    }, [gqlError]);

    useEffect(() => {
        if (gqlLoading) {
            setStatus(SyncStatus.Loading);
            props.updateUi();
        }
    }, [gqlLoading]);

    return { status, fetchData: fetchData };
}
